import { toAbsoluteUrl } from '@/_metronic/helpers'
import { InputIconGroup } from '@/_metronic/partials/content/input-group/InputIconGroup'
import { FormikValues, useFormik } from 'formik'
import moment from 'moment'
import { FC, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import SVG from 'react-inlinesvg'
import { useLocation, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { useBookingFormState } from '../core/FormStateProvider'
import { useSelectTreatmentModal } from '../core/_ModalProvider'
import { ProductPrice } from '../core/_models'
import { SelectTreatmentModal } from './SelectTreatmentModal'
import { SurveySuccessModal } from './SurveySuccessModal'

/** ------- helpers: WIB time + hours check (10:00 <= t < 22:00) ------- */
const TARGET_OUTLET_ID = '75d9b555-26ce-49af-bbf0-935bf9c9624f'

const guestFormSchemes = Yup.object().shape({
  // Personal Information
  full_name: Yup.string().required('Full Name is required'),
  email_phone: Yup.string().required('Email / Phone Number is required'),
  visit_date: Yup.string().required('Visit Date is required'),
  hotel_room: Yup.string().required('Hotel / Room No. is required'),
  emergency_contact: Yup.string().required('Emergency Contact is required'),
  relationship: Yup.string().required('Relationship is required'),
  
  // Massage Preference
  has_professional_massage: Yup.string().required('Please select if you have had a professional massage before'),
  massage_type: Yup.string().required('Please select massage type preference'),
  massage_type_other: Yup.string().when('massage_type', {
    is: 'other',
    then: (schema) => schema.required('Please specify massage type'),
    otherwise: (schema) => schema.nullable(),
  }),
  pressure_preference: Yup.string().required('Please select pressure preference'),
  
  // Medical & Health Information
  has_allergies: Yup.string().required('Please select if you have any allergies / sensitivities'),
  allergies_explanation: Yup.string().when('has_allergies', {
    is: 'yes',
    then: (schema) => schema.required('Please explain your allergies/sensitivities'),
    otherwise: (schema) => schema.nullable(),
  }),
  taking_medication: Yup.string().required('Please select if you are currently taking any medication'),
  areas_to_avoid: Yup.string().required('Please select if there are any areas you wish to avoid'),
  is_pregnant: Yup.string().required('Please select if you are currently pregnant'),
  has_chronic_illnesses: Yup.string().required('Please select if you suffer from any chronic illnesses'),
  health_conditions: Yup.array().of(Yup.string()).nullable(),
  health_conditions_explanation: Yup.string().nullable(),
  
  // Waiver & Agreement
  consent_signature_name: Yup.string().required('Consent signature is required'),
  consent_signature_data: Yup.string().required('Consent signature is required'),
  policy_signature_name: Yup.string().required('Property Policy signature is required'),
  policy_signature_data: Yup.string().required('Property Policy signature is required'),
})

const guestFormInitialValues = {
  // Personal Information
  full_name: '',
  email_phone: '',
  visit_date: moment().format('MM/DD/yyyy'),
  hotel_room: '',
  emergency_contact: '',
  relationship: '',

  // Massage Preference
  has_professional_massage: '',
  massage_type: '',
  massage_type_other: '',
  pressure_preference: '',

  // Medical & Health Information
  has_allergies: '',
  allergies_explanation: '',
  taking_medication: '',
  areas_to_avoid: '',
  is_pregnant: '',
  has_chronic_illnesses: '',
  health_conditions: [] as string[],
  health_conditions_explanation: '',

  // Waiver & Agreement
  consent_signature_name: '',
  consent_signature_data: '',
  policy_signature_name: '',
  policy_signature_data: '',
}

type Props = {
  outlet_id: string
  handleSubmit: (body: any) => void | Promise<void>
  onSuccess?: () => void
}

const IntakeForm: FC<Props> = ({outlet_id, handleSubmit, onSuccess}) => {
  const {showModal, closeModal, show, showSuccess, showSuccessModal, closeSuccessModal} =
    useSelectTreatmentModal()
  const [treatments, setTreatments] = useState<Array<ProductPrice>>([])
  const {isLoading, setIsLoading} = useBookingFormState()
  const [oneTherapist, setOneTherapist] = useState(true)
  const [showClosedModal, setShowClosedModal] = useState(false)
  const [isEthicalStatementExpanded, setIsEthicalStatementExpanded] = useState(false)
  const location = useLocation()
  const {id} = useParams()
  const isSurveyCustomer = useMemo(() => {
    return location.pathname?.startsWith('/survey-customer')
  }, [location])

  useEffect(() => {
    if (!showSuccess) {
      handleReset(guestFormInitialValues)
      setTreatments([])
    } else {
      // When success modal is shown, wait a bit then switch to survey form
      const timer = setTimeout(() => {
        if (onSuccess) {
          closeSuccessModal() // Close the success modal before switching
          onSuccess()
        }
      }, 1500) // Wait 1.5 seconds to show success message, then switch
      return () => clearTimeout(timer)
    }
  }, [showSuccess, onSuccess, closeSuccessModal])

  const onSubmit = (values: FormikValues) => {
    const body = {
      outlet_guid: outlet_id,
      employee_guid: '',
      customer_guid: '',
      customer_name: values.full_name,
      customer_phone: values.email_phone,
      customer_gender: '',
      type_order: 'walk in',
      room_number: values.hotel_room ? parseInt(values.hotel_room) : null,
      type_facility: 'cabin',
      booking_date: moment(values.visit_date, 'MM/DD/yyyy').format('yyyy-MM-DD'),
      booking_time: moment().format('HH:mm:ss'),
      notes: JSON.stringify({
        email_phone: values.email_phone,
        hotel_room: values.hotel_room,
        emergency_contact: values.emergency_contact,
        relationship: values.relationship,
        massage_preference: {
          has_professional_massage: values.has_professional_massage,
          massage_type: values.massage_type,
          massage_type_other: values.massage_type_other,
          pressure_preference: values.pressure_preference,
        },
        medical_info: {
          has_allergies: values.has_allergies,
          allergies_explanation: values.allergies_explanation,
          taking_medication: values.taking_medication,
          areas_to_avoid: values.areas_to_avoid,
          is_pregnant: values.is_pregnant,
          has_chronic_illnesses: values.has_chronic_illnesses,
          health_conditions: values.health_conditions,
          health_conditions_explanation: values.health_conditions_explanation,
        },
        waiver_agreement: {
          consent_signature_name: values.consent_signature_name,
          consent_signature_data: values.consent_signature_data,
          policy_signature_name: values.policy_signature_name,
          policy_signature_data: values.policy_signature_data,
        },
      }),
      product: treatments?.map((x) => ({
        sku: x.sku,
        qty: x.qty,
      })),
      keluhan: '',
      is_send_notification: null,
      therapist_guid: '',
    }

    setIsLoading(true)
    handleSubmit(body)
    // The form switch will be handled by the useEffect that watches showSuccess
  }

  useEffect(() => {
    if (!showSuccessModal) {
      handleReset(guestFormInitialValues)
    }
  }, [showSuccessModal])

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleReset,
    setFieldValue,
    setFieldTouched,
    handleSubmit: handleFormSubmit,
    isValid,
  } = useFormik({
    initialValues: guestFormInitialValues,
    validationSchema: guestFormSchemes,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  })

  const healthConditions = [
    'Cancer',
    'Fibromyalgia',
    'Diabetes',
    'Blood Clots',
    'Sprain / Orthopedic Injury',
    'Headaches / Migraines',
    'Stroke',
    'Kidney Disorder',
    'High/Low Blood Pressure',
    'Arthritis',
    'Heart Attack',
    'Joint Replacement',
    'Numbness / Neuropathy',
  ]

  const handleHealthConditionChange = (condition: string, checked: boolean) => {
    const currentConditions = values.health_conditions || []
    if (checked) {
      setFieldValue('health_conditions', [...currentConditions, condition])
    } else {
      setFieldValue(
        'health_conditions',
        currentConditions.filter((c) => c !== condition)
      )
    }
  }

  const handleSignatureNameChange = (field: string, name: string) => {
    setFieldValue(field, name)
    // Generate signature preview from name
    if (name) {
      const canvas = document.createElement('canvas')
      canvas.width = 400
      canvas.height = 100
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.font = 'italic 24px Georgia, serif'
        ctx.fillStyle = '#000'
        ctx.textAlign = 'center'
        ctx.fillText(name, canvas.width / 2, canvas.height / 2 + 10)
        const dataUrl = canvas.toDataURL()
        if (field === 'consent_signature_name') {
          setFieldValue('consent_signature_data', dataUrl)
        } else {
          setFieldValue('policy_signature_data', dataUrl)
        }
      }
    }
  }

  const clearSignatures = () => {
    setFieldValue('consent_signature_name', '')
    setFieldValue('consent_signature_data', '')
    setFieldValue('policy_signature_name', '')
    setFieldValue('policy_signature_data', '')
  }

  const ClosedModal: FC<{show: boolean; onClose: () => void}> = ({show, onClose}) => (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton className='justify-content-center border-0 pb-0'>
        <Modal.Title className='w-100 text-center fw-bold fs-3'>We’re Closed</Modal.Title>
      </Modal.Header>

      <Modal.Body className='text-center'>
        {/* lock icon */}
        <SVG src={toAbsoluteUrl('/media/svg/general/lock.svg')} className='me-2 align-text-top' />

        {/* responsive illustration (fluid, centered, with a sensible max width) */}
        <img
          src={toAbsoluteUrl('/media/illustrations/sigma-1/5.png')}
          alt='Closed illustration'
          className='img-fluid d-block mx-auto mb-4'
          style={{maxWidth: 360, width: '100%', height: 'auto'}}
        />

        <p className='mt-3 mb-1'>
          Sorry, our operating hours are <b>10:00 – 22:00 WIB</b>.
        </p>
        <small className='text-muted'>Please select a time within operating hours.</small>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          OK
        </Button>
      </Modal.Footer>
    </Modal>
  )

  return (
    <>
      <form onSubmit={handleFormSubmit}>
        {/* Personal Information Section */}
        <div className='mb-8'>
          <h3 className='mb-5 fw-bold'>Personal Information</h3>
        <div className='row'>
            <div className='col-12 mb-5'>
              <InputIconGroup
                label='Full Name'
                placeholder='Guest Name'
                isValid={Boolean(values.full_name)}
                errorMessage={
                  !Boolean(values.full_name) && touched?.full_name
                    ? errors?.full_name?.toString()
                    : ''
                }
                value={values.full_name}
                onChange={handleChange('full_name')}
                onBlur={handleBlur('full_name')}
                icon='/media/svg/orders/user.svg'
                disabled={!outlet_id}
              />
            </div>
            <div className='col-12 mb-5'>
              <InputIconGroup
                label='Email & Phone'
                placeholder='Email / Phone Number'
                isValid={Boolean(values.email_phone)}
                errorMessage={
                  !Boolean(values.email_phone) && touched?.email_phone
                    ? errors?.email_phone?.toString()
                    : ''
                }
                value={values.email_phone}
                onChange={handleChange('email_phone')}
                onBlur={handleBlur('email_phone')}
                icon='/media/svg/orders/phone.svg'
                disabled={!outlet_id}
              />
            </div>
            <div className='col-12 mb-5'>
              <label htmlFor='visit_date' className='form-label'>
                Visit Date
              </label>
              <DateRangePicker
                initialSettings={{
                  singleDatePicker: true,
                  showDropdowns: true,
                  parentEl: '#visit-date-picker',
                  startDate: values.visit_date
                    ? moment(values.visit_date, 'MM/DD/yyyy').toDate()
                    : moment().toDate(),
                  locale: {
                    format: 'MM/DD/yyyy',
                  },
                }}
                onCallback={(start) => {
                  setFieldValue('visit_date', start.format('MM/DD/yyyy'))
                }}
              >
                <div
                  className='d-flex align-items-center position-relative w-100'
                  id='visit-date-picker'
                >
                  <InputIconGroup
                    type='text'
                    id='visit_date'
                    placeholder='mm/dd/yyyy'
                    value={values.visit_date}
                    readOnly
                    icon='/media/svg/orders/calendar.svg'
                    disabled={!outlet_id}
                  />
                </div>
              </DateRangePicker>
              {touched.visit_date && errors.visit_date && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{errors.visit_date}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='col-12 mb-5'>
              <InputIconGroup
                label='Hotel / Room No.'
                placeholder='Hotel Name & Room No'
                isValid={true}
                value={values.hotel_room}
                onChange={handleChange('hotel_room')}
                onBlur={handleBlur('hotel_room')}
                icon='/media/svg/orders/user.svg'
                disabled={!outlet_id}
              />
            </div>
            <div className='col-12 mb-5'>
              <InputIconGroup
                label='Emergency Contact'
                placeholder='Name & Number of Emergency Contact'
                isValid={true}
                value={values.emergency_contact}
                onChange={handleChange('emergency_contact')}
                onBlur={handleBlur('emergency_contact')}
                icon='/media/svg/orders/phone.svg'
                disabled={!outlet_id}
              />
            </div>
            <div className='col-12 mb-5'>
              <InputIconGroup
                label='Relationship'
                placeholder='Relationship to Emergency Contact'
                isValid={true}
                value={values.relationship}
                onChange={handleChange('relationship')}
                onBlur={handleBlur('relationship')}
                icon='/media/svg/orders/user.svg'
                disabled={!outlet_id}
              />
            </div>
          </div>
        </div>

        <hr className='my-8' />

        {/* Massage Preference Section */}
        <div className='mb-8'>
          <h3 className='mb-5 fw-bold'>Massage Preference</h3>
          <div className='row'>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>Have you had a professional massage before?</label>
              <div className='d-flex gap-5'>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='has_professional_massage'
                    value='yes'
                    checked={values.has_professional_massage === 'yes'}
                    onChange={handleChange('has_professional_massage')}
                  />
                  <span className='form-check-label ps-2'>Yes</span>
                </label>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='has_professional_massage'
                    value='no'
                    checked={values.has_professional_massage === 'no'}
                    onChange={handleChange('has_professional_massage')}
                  />
                  <span className='form-check-label ps-2'>No</span>
                </label>
              </div>
            </div>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>What kind of massage do you prefer?</label>
              <div className='d-flex flex-column gap-3'>
                <label className='form-check form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='massage_type'
                    value='relaxation'
                    checked={values.massage_type === 'relaxation'}
                    onChange={handleChange('massage_type')}
                  />
                  <span className='form-check-label ps-2'>Relaxation</span>
                </label>
                <label className='form-check form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='massage_type'
                    value='therapeutic'
                    checked={values.massage_type === 'therapeutic'}
                    onChange={handleChange('massage_type')}
                  />
                  <span className='form-check-label ps-2'>Therapeutic / Deep Tissue</span>
                </label>
                <div className='d-flex align-items-center gap-3'>
                  <label className='form-check form-check-solid'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='massage_type'
                      value='other'
                      checked={values.massage_type === 'other'}
                      onChange={handleChange('massage_type')}
                    />
                    <span className='form-check-label ps-2'>Other:</span>
                  </label>
                  {values.massage_type === 'other' && (
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Specify'
                      value={values.massage_type_other}
                      onChange={handleChange('massage_type_other')}
                      style={{maxWidth: '300px'}}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>What pressure do you prefer?</label>
              <div className='d-flex gap-5'>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='pressure_preference'
                    value='light'
                    checked={values.pressure_preference === 'light'}
                    onChange={handleChange('pressure_preference')}
                  />
                  <span className='form-check-label ps-2'>Light</span>
                </label>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='pressure_preference'
                    value='medium'
                    checked={values.pressure_preference === 'medium'}
                    onChange={handleChange('pressure_preference')}
                  />
                  <span className='form-check-label ps-2'>Medium</span>
                </label>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='pressure_preference'
                    value='strong'
                    checked={values.pressure_preference === 'strong'}
                    onChange={handleChange('pressure_preference')}
                  />
                  <span className='form-check-label ps-2'>Strong</span>
              </label>
              </div>
            </div>
          </div>
        </div>

        <hr className='my-8' />

        {/* Medical & Health Information Section */}
        <div className='mb-8'>
          <h3 className='mb-5 fw-bold'>Medical & Health Information</h3>
          <div className='row'>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>Do you have any allergies / sensitivities?</label>
              <div className='d-flex gap-5 mb-3'>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='has_allergies'
                    value='yes'
                    checked={values.has_allergies === 'yes'}
                    onChange={handleChange('has_allergies')}
                  />
                  <span className='form-check-label ps-2'>Yes</span>
                </label>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='has_allergies'
                    value='no'
                    checked={values.has_allergies === 'no'}
                    onChange={handleChange('has_allergies')}
                  />
                  <span className='form-check-label ps-2'>No</span>
                </label>
              </div>
              {values.has_allergies === 'yes' && (
                <textarea
                  className='form-control'
                  rows={3}
                  placeholder='Please explain your allergies/sensitivities'
                  value={values.allergies_explanation}
                  onChange={handleChange('allergies_explanation')}
                  onBlur={handleBlur('allergies_explanation')}
                />
              )}
              {touched.allergies_explanation && errors.allergies_explanation && (
                <div className='fv-plugins-message-container mt-2'>
                  <div className='fv-help-block'>
                    <span role='alert' className='text-danger'>
                      {errors.allergies_explanation}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>Are you currently taking any medication?</label>
              <div className='d-flex gap-5'>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='taking_medication'
                    value='yes'
                    checked={values.taking_medication === 'yes'}
                    onChange={handleChange('taking_medication')}
                  />
                  <span className='form-check-label ps-2'>Yes</span>
                </label>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='taking_medication'
                    value='no'
                    checked={values.taking_medication === 'no'}
                    onChange={handleChange('taking_medication')}
                  />
                  <span className='form-check-label ps-2'>No</span>
                </label>
              </div>
            </div>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>
                Are there any areas you wish to avoid? (feet, face, abdomen, etc.)
              </label>
              <div className='d-flex gap-5'>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='areas_to_avoid'
                    value='yes'
                    checked={values.areas_to_avoid === 'yes'}
                    onChange={handleChange('areas_to_avoid')}
                  />
                  <span className='form-check-label ps-2'>Yes</span>
                </label>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='areas_to_avoid'
                    value='no'
                    checked={values.areas_to_avoid === 'no'}
                    onChange={handleChange('areas_to_avoid')}
                  />
                  <span className='form-check-label ps-2'>No</span>
                </label>
              </div>
            </div>
            <div className='col-12 mb-5'>
              <div className='alert alert-danger d-flex align-items-center p-4 mb-3' role='alert'>
                <i className='fas fa-exclamation-triangle fs-2x text-danger me-4'></i>
                <div className='d-flex flex-column'>
                  <label className='form-label mb-0 fw-bold'>Are you currently pregnant?</label>
                  <div className='d-flex gap-5 mt-3'>
                    <label className='form-check form-check-inline form-check-solid'>
                      <input
                        className='form-check-input'
                        type='radio'
                        name='is_pregnant'
                        value='yes'
                        checked={values.is_pregnant === 'yes'}
                        onChange={handleChange('is_pregnant')}
                      />
                      <span className='form-check-label ps-2'>Yes</span>
                    </label>
                    <label className='form-check form-check-inline form-check-solid'>
                      <input
                        className='form-check-input'
                        type='radio'
                        name='is_pregnant'
                        value='no'
                        checked={values.is_pregnant === 'no'}
                        onChange={handleChange('is_pregnant')}
                      />
                      <span className='form-check-label ps-2'>No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>Do you suffer from any chronic illnesses?</label>
              <div className='d-flex gap-5'>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='has_chronic_illnesses'
                    value='yes'
                    checked={values.has_chronic_illnesses === 'yes'}
                    onChange={handleChange('has_chronic_illnesses')}
                  />
                  <span className='form-check-label ps-2'>Yes</span>
                </label>
                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='has_chronic_illnesses'
                    value='no'
                    checked={values.has_chronic_illnesses === 'no'}
                    onChange={handleChange('has_chronic_illnesses')}
                  />
                  <span className='form-check-label ps-2'>No</span>
                </label>
              </div>
            </div>
            <div className='col-12 mb-5'>
              <label className='form-label mb-3'>
                Please check all relevant health conditions:
              </label>
              <div className='row'>
                {healthConditions.map((condition, index) => (
                  <div key={condition} className='col-md-4 mb-3'>
                    <label className='form-check form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={(values.health_conditions || []).includes(condition)}
                        onChange={(e) => handleHealthConditionChange(condition, e.target.checked)}
                      />
                      <span className='form-check-label ps-2'>{condition}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className='col-12 mb-5'>
              <textarea
                className='form-control'
                rows={4}
                placeholder='Explain marked conditions (including orthopedic injuries) and what makes them better/worse.'
                value={values.health_conditions_explanation}
                onChange={handleChange('health_conditions_explanation')}
                onBlur={handleBlur('health_conditions_explanation')}
              />
            </div>
          </div>
        </div>

        <hr className='my-8' />

        {/* Waiver & Agreement Section */}
        <div className='mb-8'>
          <h2 className='mb-5 fw-bold text-danger' style={{fontSize: '2rem'}}>
            Waiver & Agreement
          </h2>

          {/* Massage Therapy Consent */}
          <div className='mb-6'>
            <h4 className='mb-3 fw-bold'>Massage Therapy Consent:</h4>
            <div
              className='form-control'
              style={{
                height: '150px',
                overflowY: 'auto',
                whiteSpace: 'pre-line',
                backgroundColor: '#fff',
              }}
            >
              I understand that massage is intended to reduce stress, promote relaxation, relieve
              muscle tension, and improve circulation and energy flow. If I experience pain or
              discomfort during the session, I will immediately inform the therapist so that the
              pressure/technique can be adjusted to my comfort level. I will not hold the therapist
              liable for any pain or discomfort I experience during or after the session.{'\n\n'}I
              understand that the services provided today are not a substitute for medical
              treatment. I understand that the massage is purely therapeutic and not sexual in
              nature.
            </div>
          </div>

          {/* Ethical & Legal Statement */}
          <div className='mb-6'>
            <h4 className='mb-3 fw-bold'>
              Ethical & Legal Statement <span className='text-danger'>(Important)</span>:
            </h4>
            <div className='form-control' style={{whiteSpace: 'pre-line', backgroundColor: '#fff'}}>
              I understand that any form of indecent, sexual harassment, or immoral conduct violates
              decency norms and{' '}
              {!isEthicalStatementExpanded && (
                <span
                  className='text-primary cursor-pointer'
                  onClick={() => setIsEthicalStatementExpanded(true)}
                  style={{cursor: 'pointer'}}
                >
                  <i className='fas fa-chevron-down'></i>
                </span>
              )}
              {isEthicalStatementExpanded && (
                <>
                  {' '}
                  is strictly prohibited. Any violation of this policy will result in immediate
                  termination of services and may lead to legal action. I agree to maintain
                  professional boundaries and respect the therapist at all times during the session.
                  <br />
                  <span
                    className='text-primary cursor-pointer'
                    onClick={() => setIsEthicalStatementExpanded(false)}
                    style={{cursor: 'pointer'}}
                  >
                    <i className='fas fa-chevron-up'></i>
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Guest Name & Signature (Consent) */}
          <div className='mb-6'>
            <h4 className='mb-3 fw-bold'>Guest Name & Signature (Consent)</h4>
            <div className='d-flex gap-3 mb-3'>
              <button type='button' className='btn btn-light d-flex align-items-center' disabled>
                <i className='fas fa-keyboard me-2'></i>
                Type Signature
              </button>
            </div>
            <input
              type='text'
              className='form-control mb-3'
              placeholder='Type your full name to sign'
              value={values.consent_signature_name}
              onChange={(e) => handleSignatureNameChange('consent_signature_name', e.target.value)}
            />
            <div
              className='border rounded p-3 text-center bg-light'
              style={{
                minHeight: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {values.consent_signature_data ? (
                <img
                  src={values.consent_signature_data}
                  alt='Signature Preview'
                  style={{maxHeight: '60px', maxWidth: '100%'}}
                />
              ) : (
                <span
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    fontSize: '18px',
                    color: '#999',
                  }}
                >
                  Signature Preview
                </span>
              )}
            </div>
          </div>

          {/* Guest Name & Signature (Property Policy) */}
          <div className='mb-6'>
            <h4 className='mb-3 fw-bold'>Guest Name & Signature (Property Policy)</h4>
            <div className='d-flex gap-3 mb-3'>
              <button type='button' className='btn btn-light d-flex align-items-center' disabled>
                <i className='fas fa-keyboard me-2'></i>
                Type Signature
              </button>
            </div>
            <input
              type='text'
              className='form-control mb-3'
              placeholder='Type your full name to sign'
              value={values.policy_signature_name}
              onChange={(e) => handleSignatureNameChange('policy_signature_name', e.target.value)}
            />
            <div
              className='border rounded p-3 text-center bg-light'
              style={{
                minHeight: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {values.policy_signature_data ? (
                <img
                  src={values.policy_signature_data}
                  alt='Signature Preview'
                  style={{maxHeight: '60px', maxWidth: '100%'}}
                />
              ) : (
                <span
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    fontSize: '18px',
                    color: '#999',
                  }}
                >
                  Signature Preview
                </span>
              )}
            </div>
          </div>

          {/* Clear Signatures Link */}
          <div className='text-end mb-4'>
            <button
              type='button'
              className='btn btn-link text-danger p-0'
              onClick={clearSignatures}
            >
              <i className='fas fa-times me-1'></i>
              Clear Signatures
            </button>
          </div>
        </div>

        <div className='col-12'>
          <button
            className='btn btn-md btn-info w-100'
            type='submit'
            disabled={!isValid || isLoading || !outlet_id}
          >
            {isLoading ? (
              <Spinner color='white' size='sm' animation='border' />
            ) : (
              'Proceed to Post-Treatment Survey'
            )}
          </button>
        </div>
        <SurveySuccessModal
          onReserve={() => {
            handleReset(guestFormInitialValues)
            setTreatments([])
            closeSuccessModal()
          }}
        />
      </form>

      {/* 🔔 We're Closed modal */}
      <ClosedModal show={showClosedModal} onClose={() => setShowClosedModal(false)} />

      {show && (
        <SelectTreatmentModal
          outlet_id={outlet_id}
          ids='walkin'
          prevData={treatments}
          oneTherapist={!oneTherapist}
          onSave={(data: Array<ProductPrice>) => {
            setTreatments(data)
            closeModal()
          }}
        />
      )}
    </>
  )
}

export default IntakeForm
